import React, { useEffect, lazy, Suspense, useMemo } from 'react';
import PropTypes from 'prop-types';
import './index.css';
import './App.css';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { CssBaseline, Box, Alert, CircularProgress } from '@mui/material';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import { FontSizeProvider, useFontSize } from './context/FontSizeContext';
import { UIProvider } from './context/UIContext';
import { AuthProvider } from './context/AuthContext';
import { ResourceProvider } from './context/ResourceContext';
import { MessageContextProvider } from './context/MessageContext';
import ResourceContextProvider from './context/ResourceContextProvider';
import { ViewSpecificContextProvider } from './context/ViewSpecificContext';
import { DragDropProvider } from './context/DragDropContext';
import { SessionProvider } from './context/SessionContext';
import { TtsProvider } from './context/TtsContext';
import { VideoProvider } from './context/VideoContext';
import { ChatSessionProvider } from './context/ChatSessionContext';
import { DateProvider } from './context/DateContext';
import { SystemStatusProvider } from './context/SystemStatusContext';
import { AlertProvider } from './context/AlertContext';
import ErrorBoundary from './components/common/ErrorBoundary';
import { AppEventsProvider } from './context/AppEventsContext';
import { EnhancementProvider } from './context/EnhancementContext';
import { TourProvider } from './context/TourContext';
import OnboardingTour from './components/onboarding/OnboardingTour';
import PrivateRoute from './components/auth/PrivateRoute';
import ChatContainer from './components/chat/ChatContainer';
import ContentPanel from './components/content/ContentPanel';
import SystemMessageArea from './components/common/SystemMessageArea';
import { ConfigProvider } from './context/ConfigContext';
import { setAppConfig } from './config/appConfig';
import VerificationGuard from './components/auth/VerificationGuard';
import { ModalBackHandlerProvider } from './context/ModalBackHandlerContext';
import { useVersionChecker } from './hooks/useVersionChecker';
import { ContentTypeProvider } from './context/ContentTypeContext';

const LoginPage = lazy(() => import('./components/auth/LoginPage'));
const RegisterPage = lazy(() => import('./components/auth/RegisterPage'));
const RequestPasswordResetPage = lazy(() => import('./components/auth/RequestPasswordResetPage'));
const ResetPasswordPage = lazy(() => import('./components/auth/ResetPasswordPage'));
const VerifyEmailPage = lazy(() => import('./components/auth/VerifyEmailPage'));
const LeftSidebar = lazy(() => import('./components/layout/LeftSidebar'));
const RightSidebar = lazy(() => import('./components/layout/RightSidebar'));
const AppLayout = lazy(() => import('./components/layout/AppLayout'));
const PublicResourcePage = lazy(() => import('./components/public/PublicResourcePage'));

const DefaultAppProviders = ({ children }) => <>{children}</>;
const ProtectedAppProviders = ({ children, OuterAppProviders, InnerAppProviders, onAppResourceSync, sseEventFilter, sessionInitStrategy }) => (
  <OuterAppProviders>
    <ResourceProvider>
      <AppEventsProvider>
        <InnerAppProviders>
          <TourProvider>
            <DateProvider>
              <ChatSessionProvider>
                <DragDropProvider>
                  <TtsProvider>
                    <VideoProvider>
                      <MessageContextProvider>
                        <ViewSpecificContextProvider>
                          <EnhancementProvider>
                            <AlertProvider>
                              <SessionProvider onResourceSync={onAppResourceSync} sseEventFilter={sseEventFilter} sessionInitStrategy={sessionInitStrategy}>
                                <ResourceContextProvider>
                                  {children}
                                </ResourceContextProvider>
                              </SessionProvider>
                            </AlertProvider>
                          </EnhancementProvider>
                        </ViewSpecificContextProvider>
                      </MessageContextProvider>
                    </VideoProvider>
                  </TtsProvider>
                </DragDropProvider>
              </ChatSessionProvider>
              <OnboardingTour />
            </DateProvider>
          </TourProvider>
        </InnerAppProviders>
      </AppEventsProvider>
    </ResourceProvider>
  </OuterAppProviders>
);
const LoadingFallback = () => (
  <Box sx={{ height: '100%', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <CircularProgress size={30} />
  </Box>
);
const MainAppLayout = () => (
  <Suspense fallback={<LoadingFallback />}>
    <AppLayout
      leftSidebar={
        <Suspense fallback={<Box width={280} />}>
          <LeftSidebar />
        </Suspense>
      }
      contentPanel={<ContentPanel />}
      messageArea={<SystemMessageArea />}
      chatArea={<ChatContainer />}
      rightSidebar={
        <Suspense fallback={<Box width={60} />}>
          <RightSidebar />
        </Suspense>
      }
    />
  </Suspense>
);
const AppWithMuiTheme = ({ getAppTheme, children }) => {
  const { isDarkMode, themeId } = useTheme();
  const { fontSizeMultiplier } = useFontSize();
  const theme = getAppTheme(isDarkMode ? 'dark' : 'light', fontSizeMultiplier, themeId);
  return <MuiThemeProvider theme={theme}>{children}</MuiThemeProvider>;
};
AppWithMuiTheme.propTypes = {
  getAppTheme: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
};
function CoreApp({
  OuterAppProviders = DefaultAppProviders,
  InnerAppProviders = DefaultAppProviders,
  registerAppComponents,
  registerSyncDrivers,
  onAppResourceSync,
  config,
  eventHandlerInitializer,
  loginPageFeatures,
  sseEventFilter,
  getAppTheme,
  sessionInitStrategy,
  buildInfo,
}) {
  const enhancedConfig = useMemo(() => ({
    ...config,
    buildInfo,
  }), [config, buildInfo]);
  if (enhancedConfig) {
    setAppConfig(enhancedConfig);
  }
  useVersionChecker(buildInfo);
  useEffect(() => {
    if (registerAppComponents) {
      registerAppComponents();
    }
    if (registerSyncDrivers) {
      registerSyncDrivers();
    }
  }, [registerAppComponents, registerSyncDrivers]);
  if (!config || !getAppTheme) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">
          Application Configuration Error: The core platform was not provided with a configuration object or a getAppTheme function. The application cannot start.
        </Alert>
      </Box>
    );
  }
  return (
    <ErrorBoundary>
      <Router>
        <ModalBackHandlerProvider>
          <AuthProvider>
            <SystemStatusProvider>
              <ConfigProvider config={enhancedConfig}>
                <FontSizeProvider>
                  <ThemeProvider>
                    <AppWithMuiTheme getAppTheme={getAppTheme}>
                      <CssBaseline />
                      <UIProvider>
                        <Suspense fallback={<Box sx={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><CircularProgress /></Box>}>
                          <Routes>
                            <Route path="/login" element={<LoginPage featuresComponent={loginPageFeatures} />} />
                            <Route path="/register" element={<RegisterPage />} />
                            <Route path="/forgot-password" element={<RequestPasswordResetPage />} />
                            <Route path="/reset-password" element={<ResetPasswordPage />} />
                            <Route
                              path="/verify-email"
                              element={
                                <PrivateRoute>
                                  <VerifyEmailPage />
                                </PrivateRoute>
                              }
                            />
                            
                            {/* --- NEW PUBLIC ROUTE --- */}
                            <Route 
                              path="/public/resource/:resourceType/:resourceId" 
                              element={
                                <ResourceProvider>
                                  <DateProvider>
                                    <VideoProvider>
                                      <EnhancementProvider>
                                        <ContentTypeProvider contentTypes={config.contentTypes}>
                                          <PublicResourcePage />
                                        </ContentTypeProvider>
                                      </EnhancementProvider>
                                    </VideoProvider>
                                  </DateProvider>
                                </ResourceProvider>
                              } 
                            />
                            {/* ------------------------ */}

                            <Route
                              path="/*"
                              element={
                                <PrivateRoute>
                                  <VerificationGuard>
                                    <ProtectedAppProviders
                                      OuterAppProviders={OuterAppProviders}
                                      InnerAppProviders={InnerAppProviders}
                                      onAppResourceSync={onAppResourceSync}
                                      sseEventFilter={sseEventFilter}
                                      sessionInitStrategy={sessionInitStrategy}
                                    >
                                      {eventHandlerInitializer}
                                      <MainAppLayout />
                                    </ProtectedAppProviders>
                                  </VerificationGuard>
                                </PrivateRoute>
                              }
                            />
                          </Routes>
                        </Suspense>
                      </UIProvider>
                    </AppWithMuiTheme>
                  </ThemeProvider>
                </FontSizeProvider>
              </ConfigProvider>
            </SystemStatusProvider>
          </AuthProvider>
        </ModalBackHandlerProvider>
      </Router>
    </ErrorBoundary>
  );
}
CoreApp.propTypes = {
    OuterAppProviders: PropTypes.elementType,
    InnerAppProviders: PropTypes.elementType,
    registerAppComponents: PropTypes.func,
    registerSyncDrivers: PropTypes.func,
    onAppResourceSync: PropTypes.func,
    config: PropTypes.object.isRequired,
    eventHandlerInitializer: PropTypes.node,
    loginPageFeatures: PropTypes.node,
    sseEventFilter: PropTypes.func,
    getAppTheme: PropTypes.func.isRequired,
    sessionInitStrategy: PropTypes.func,
    buildInfo: PropTypes.shape({
      version: PropTypes.string,
      branch: PropTypes.string,
      commit: PropTypes.string,
      timestamp: PropTypes.string,
    }),
};
export default CoreApp;